import React, { useRef, useState } from 'react'

import { useHistory } from 'react-router-dom'

import { ExclamationCircleOutlined } from '@ant-design/icons'
import { Typography, List, message, Modal } from 'antd'

import { observer } from 'mobx-react-lite'

import { DrawerRef } from 'common/components/Drawer'
import { Loading } from 'common/components/Loading'
import { Page } from 'common/components/Page'
import { SearchInput } from 'common/components/SearchInput'
import { createURL } from 'common/helpers/algolia'
import { filterTextByFields } from 'common/helpers/filter-text-by-fields'
import { useQuery } from 'common/hooks/use-query'
import { OrderStates } from 'common/server/server_types'

import { useVendorStores } from 'vendor/hooks/use-stores'
import { VendorCompany, UpdateVendorCompany } from 'vendor/server/vendor_companies'

import { CustomerCard } from './customer_card'
import { UpdateCustomer } from './update_customer'

const CustomersPage = observer(() => {
  const { vendorCompanyStore, userStore } = useVendorStores()

  const drawerRef = useRef<DrawerRef>()

  const history = useHistory()

  const userId = userStore.currentUser?.id

  useQuery(vendorCompanyStore.getOrderStatsByCustomers)
  const { isLoading } = useQuery(vendorCompanyStore.getVendorCompanies)

  const [vendorCompanyId, setVendorCompanyId] = useState('')
  const [searchText, setSearchText] = useState('')
  const [isSubmitting, setSubmitting] = useState({})

  const handleSearchText = (text = '') => setSearchText(text?.trim())

  const handeFilterText = (vendorCompany: VendorCompany) => {
    return filterTextByFields({ fields: ['name'], obj: { name: vendorCompany?.company?.name }, searchText })
  }

  const updateCustomer = async (vendorCompanyParams: UpdateVendorCompany, isWatching = false, companyName) => {
    setSubmitting({ [vendorCompanyParams.id]: true })

    try {
      await vendorCompanyStore.updateVendorCompany(vendorCompanyParams)
      vendorCompanyStore.getVendorCompanies()

      message.success(
        <>
          {isWatching ? `Stopped watching ${companyName}.` : `Now watching ${companyName}.`}
          {vendorCompanyParams.apply_retroactively && (
            <>
              <br />
              The orders can take time to update. Please allow a few seconds for your changes to appear.
            </>
          )}
        </>,
      )
    } catch (error) {
      if (error?.response?.data?.error) {
        message.error(error.response.data.error)
      } else {
        message.error(isWatching ? 'Unable to unwatch' : 'Unable to watch')
      }
    } finally {
      setSubmitting({})
    }
  }

  const handeToggleWatcher = async (vendorCompany: VendorCompany) => {
    const isWatching = vendorCompany?.default_watcher_vendor_user_ids.includes(userId)

    const params = {
      id: vendorCompany?.id,
      default_watcher_vendor_user_ids: isWatching
        ? vendorCompany?.default_watcher_vendor_user_ids.filter((id) => id !== userId)
        : [...vendorCompany?.default_watcher_vendor_user_ids, userId],
    }

    Modal.confirm({
      title: 'Do you want these changes to apply to previous orders or only move forward?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes',
      cancelText: 'No',
      onOk() {
        updateCustomer({ ...params, apply_retroactively: true }, isWatching, vendorCompany?.company?.name)
      },
      onCancel() {
        updateCustomer({ ...params, apply_retroactively: false }, isWatching, vendorCompany?.company?.name)
      },
    })
  }

  if (isLoading) {
    return <Loading />
  }

  const watchedCustomers = vendorCompanyStore.vendorCompanies.filter((vendorCompany) =>
    vendorCompany.default_watcher_vendor_user_ids.includes(userId),
  )
  const unwatchedCustomers = vendorCompanyStore.vendorCompanies.filter(
    (vendorCompany) => !vendorCompany.default_watcher_vendor_user_ids.includes(userId),
  )

  const orderStats = vendorCompanyStore.orderStats

  return (
    <Page>
      <Page.Header>
        <Typography.Title level={3}>Customers</Typography.Title>

        <SearchInput onSearch={handleSearchText} />
      </Page.Header>

      <UpdateCustomer ref={drawerRef} onClose={() => drawerRef.current?.close()} vendorCompanyId={vendorCompanyId} />

      <Page.Content>
        <List
          grid={{
            gutter: 16,
            xs: 1,
            sm: 2,
            md: 2,
            lg: 3,
            xl: 3,
            xxl: 4,
          }}
          loading={isLoading}
          dataSource={[...watchedCustomers, ...unwatchedCustomers].filter(handeFilterText)}
          renderItem={(vendorCompany) => (
            <List.Item
              onClick={() => {
                const searchState = {
                  refinementList: {
                    'company.name': vendorCompany.company?.name,
                    'vendor_users.id': '',
                  },
                }
                const params = createURL(searchState)
                history.push('/vendor/orders' + params)
              }}
            >
              <CustomerCard
                title={vendorCompany.company?.name}
                logoUrl={vendorCompany.company?.logo?.url}
                isWatching={vendorCompany?.default_watcher_vendor_user_ids.includes(userId)}
                onToggleWatcher={() => handeToggleWatcher(vendorCompany)}
                quotedCount={orderStats?.[vendorCompany.company?.id]?.[OrderStates.QUOTED]}
                orderedCount={orderStats?.[vendorCompany.company?.id]?.[OrderStates.ORDERED]}
                shippedCount={
                  (orderStats?.[vendorCompany.company?.id]?.[OrderStates.SHIPPED] || 0) +
                  (orderStats?.[vendorCompany.company?.id]?.[OrderStates.PARTIALLY_SHIPPED] || 0)
                }
                deliveredCount={orderStats?.[vendorCompany.company?.id]?.[OrderStates.DELIVERED]}
                isLoading={isSubmitting[vendorCompany.id]}
                onClickSettings={(event) => {
                  event.stopPropagation()
                  setVendorCompanyId(vendorCompany?.id)
                  drawerRef.current?.show()
                }}
              />
            </List.Item>
          )}
        />
      </Page.Content>
    </Page>
  )
})

export default CustomersPage
